import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import { DownloadOptions, DownloadProgress } from '../types';
import { useAppStore } from '../stores/appStore';

/**
 * 图片处理工具类
 */
export class ImageProcessor {
  /**
   * 将图片URL转换为Base64
   */
  static async urlToBase64(url: string): Promise<string> {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      throw new Error('图片转换失败: ' + error);
    }
  }

  /**
   * 将Base64转换为Blob
   */
  static base64ToBlob(base64: string, mimeType: string = 'image/png'): Blob {
    const byteCharacters = atob(base64.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }

  /**
   * 调整图片尺寸
   */
  static async resizeImage(
    imageSrc: string,
    width: number,
    height: number,
    quality: number = 0.9
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('无法创建画布上下文'));
          return;
        }

        canvas.width = width;
        canvas.height = height;

        // 使用高质量缩放
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // 绘制图片
        ctx.drawImage(img, 0, 0, width, height);

        // 转换为指定格式
        const format = width <= 256 ? 'image/png' : 'image/jpeg';
        const dataUrl = canvas.toDataURL(format, quality);
        resolve(dataUrl);
      };

      img.onerror = () => reject(new Error('图片加载失败'));
      img.src = imageSrc;
    });
  }

  /**
   * 转换图片格式
   */
  static async convertImageFormat(
    imageSrc: string,
    format: 'png' | 'jpeg' | 'webp' | 'ico' | 'svg',
    quality: number = 0.9
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        if (format === 'svg') {
          const width = img.width;
          const height = img.height;
          const svgHeader = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>`;
          const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><image href="${imageSrc}" width="${width}" height="${height}"/></svg>`;
          const blob = new Blob([svgHeader + svgContent], { type: 'image/svg+xml' });
          resolve(blob);
        } else {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('无法创建画布上下文'));
            return;
          }
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('图片转换失败'));
            }
          }, `image/${format}`, quality);
        }
      };

      img.onerror = () => reject(new Error('图片加载失败'));
      img.src = imageSrc;
    });
  }

  /**
   * 创建图标文件（.ico格式）
   */
  static async createIcon(imageSrc: string, sizes: number[] = [16, 32, 48, 64, 128, 256]): Promise<Blob> {
    // 这里简化处理，实际ICO格式需要更复杂的处理
    // 返回最大尺寸的PNG作为替代
    const resizedImage = await this.resizeImage(imageSrc, 256, 256, 1.0);
    return this.base64ToBlob(resizedImage, 'image/png');
  }
}

/**
 * 下载服务类
 */
export class DownloadService {
  private static instance: DownloadService;
  private store = useAppStore.getState();

  private constructor() {}

  static getInstance(): DownloadService {
    if (!DownloadService.instance) {
      DownloadService.instance = new DownloadService();
    }
    return DownloadService.instance;
  }

  static downloadImage = DownloadService.getInstance().downloadImage;

  /**
   * 下载图片
   */
  async downloadImage(
    imageUrl: string,
    filename: string,
    options: DownloadOptions
  ): Promise<void> {
    const progressId = `download_${Date.now()}`;
    
    try {
      // 添加下载进度
      const progress: DownloadProgress = {
        id: progressId,
        filename,
        progress: 0,
        status: 'pending',
      };
      this.store.addDownloadProgress(progress);

      // 更新进度为下载中
      this.store.updateDownloadProgress(progressId, {
        status: 'downloading',
        progress: 10,
      });

      // 处理图片
      let processedBlob: Blob;
      
      if (options.format === 'ico') {
        processedBlob = await ImageProcessor.createIcon(imageUrl);
      } else if (options.format === 'svg') {
        const sizeW = options.size?.width || 512;
        const sizeH = options.size?.height || 512;
        const base64 = await ImageProcessor.urlToBase64(imageUrl);
        const svgHeader = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>`;
        const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="${sizeW}" height="${sizeH}"><image href="${base64}" width="${sizeW}" height="${sizeH}"/></svg>`;
        processedBlob = new Blob([svgHeader + svgContent], { type: 'image/svg+xml' });
      } else if (options.size) {
        // 需要调整尺寸
        const resizedDataUrl = await ImageProcessor.resizeImage(
          imageUrl,
          options.size.width,
          options.size.height,
          options.quality
        );
        processedBlob = ImageProcessor.base64ToBlob(
          resizedDataUrl,
          `image/${options.format}`
        );
      } else {
        // 仅转换格式或直接保存远程图片
        if (/^https?:\/\//.test(imageUrl) && !options.size) {
          const resp = await fetch(imageUrl);
          processedBlob = await resp.blob();
        } else {
          processedBlob = await ImageProcessor.convertImageFormat(
            imageUrl,
            options.format,
            options.quality
          );
        }
      }

      // 更新进度
      this.store.updateDownloadProgress(progressId, {
        progress: 80,
      });

      // 生成文件名
      const inferredExt = processedBlob.type.startsWith('image/') ? processedBlob.type.replace('image/','') : (options.format === 'jpeg' ? 'jpg' : options.format);
      const extension = inferredExt === 'jpeg' ? 'jpg' : inferredExt;
      const finalFilename = options.filename || `${filename}.${extension}`;

      // 保存文件
      saveAs(processedBlob, finalFilename);

      // 更新进度为完成
      this.store.updateDownloadProgress(progressId, {
        progress: 100,
        status: 'completed',
      });

      // 3秒后移除进度条
      setTimeout(() => {
        this.store.removeDownloadProgress(progressId);
      }, 3000);

    } catch (error) {
      // 更新错误状态
      this.store.updateDownloadProgress(progressId, {
        status: 'error',
        error: error instanceof Error ? error.message : '下载失败',
      });

      // 5秒后移除错误进度条
      setTimeout(() => {
        this.store.removeDownloadProgress(progressId);
      }, 5000);

      throw error;
    }
  }

  /**
   * 批量下载图片
   */
  async batchDownload(
    images: Array<{ url: string; filename: string }>,
    options: DownloadOptions
  ): Promise<void> {
    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      try {
        await this.downloadImage(image.url, image.filename, options);
        // 添加延迟避免同时下载过多
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.error(`下载失败: ${image.filename}`, error);
        // 继续下载其他图片
      }
    }
  }

  /**
   * 从HTML元素创建图片并下载
   */
  async downloadElement(
    element: HTMLElement,
    filename: string,
    options: {
      width?: number;
      height?: number;
      backgroundColor?: string;
      format?: 'png' | 'jpeg' | 'webp';
      quality?: number;
    } = {}
  ): Promise<void> {
    try {
      const canvas = await html2canvas(element, {
        width: options.width,
        height: options.height,
        backgroundColor: options.backgroundColor || '#ffffff',
        scale: 2, // 高DPI
        useCORS: true,
        allowTaint: true,
      });

      const format = options.format || 'png';
      const quality = options.quality || 0.9;
      
      canvas.toBlob((blob) => {
        if (blob) {
          saveAs(blob, `${filename}.${format}`);
        }
      }, `image/${format}`, quality);
    } catch (error) {
      throw new Error('元素截图失败: ' + error);
    }
  }
}

export default {
  ImageProcessor,
  DownloadService: DownloadService.getInstance(),
};
